/**
 * Dagre Layout — Phase 6
 * Uses dagre for topology-aware directed graph layouts.
 * Ideal for flowcharts, hierarchies, and process diagrams
 * where edge direction matters.
 */

import * as dagre from 'dagre';
import type { PlacementResult } from './canvasPlacement';

interface DagreNode {
  id: string;
  width: number;
  height: number;
}

interface DagreEdge {
  source: string;
  target: string;
}

export type DagreDirection = 'TB' | 'BT' | 'LR' | 'RL';

/**
 * Compute positions for nodes in a directed graph using dagre.
 * Returns a map of node ID -> { x, y } positions (top-left corner).
 */
export function computeDagreLayout(
  nodes: DagreNode[],
  edges: DagreEdge[],
  options: {
    direction?: DagreDirection;
    nodeSep?: number;
    edgeSep?: number;
    rankSep?: number;
    centerX?: number;
    centerY?: number;
  } = {}
): Map<string, PlacementResult> {
  const {
    direction = 'TB',
    nodeSep = 50,
    edgeSep = 20,
    rankSep = 80,
    centerX = 0,
    centerY = 0,
  } = options;

  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: direction,
    nodesep: nodeSep,
    edgesep: edgeSep,
    ranksep: rankSep,
    marginx: 0,
    marginy: 0,
  });
  g.setDefaultEdgeLabel(() => ({}));

  // Add nodes
  for (const node of nodes) {
    g.setNode(node.id, { width: node.width, height: node.height });
  }

  // Add edges
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  // Run layout
  dagre.layout(g);

  // Extract positions and center the result
  const results = new Map<string, PlacementResult>();

  // First pass: compute bounding box of dagre output
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of nodes) {
    const dagreNode = g.node(node.id);
    if (!dagreNode) continue;
    // dagre returns center positions
    const x = dagreNode.x - node.width / 2;
    const y = dagreNode.y - node.height / 2;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + node.width);
    maxY = Math.max(maxY, y + node.height);
  }

  // Compute offset to center the layout around (centerX, centerY)
  const layoutCenterX = (minX + maxX) / 2;
  const layoutCenterY = (minY + maxY) / 2;
  const offsetX = centerX - layoutCenterX;
  const offsetY = centerY - layoutCenterY;

  // Second pass: apply offset
  for (const node of nodes) {
    const dagreNode = g.node(node.id);
    if (!dagreNode) continue;
    results.set(node.id, {
      x: dagreNode.x - node.width / 2 + offsetX,
      y: dagreNode.y - node.height / 2 + offsetY,
    });
  }

  return results;
}

/**
 * Determine optimal dagre direction from diagram type.
 */
export function getDagreDirection(diagramType: string): DagreDirection {
  switch (diagramType) {
    case 'flowchart':
      return 'TB'; // top-to-bottom
    case 'process':
      return 'LR'; // left-to-right
    case 'hierarchy':
      return 'TB';
    case 'mind_map':
      return 'LR';
    case 'comparison':
      return 'LR';
    default:
      return 'TB';
  }
}

/**
 * Determine optimal connector positions based on dagre direction.
 */
export function getDagreConnectors(direction: DagreDirection): {
  startConnector: 'top' | 'bottom' | 'left' | 'right';
  endConnector: 'top' | 'bottom' | 'left' | 'right';
} {
  switch (direction) {
    case 'TB':
      return { startConnector: 'bottom', endConnector: 'top' };
    case 'BT':
      return { startConnector: 'top', endConnector: 'bottom' };
    case 'LR':
      return { startConnector: 'right', endConnector: 'left' };
    case 'RL':
      return { startConnector: 'left', endConnector: 'right' };
  }
}
